import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    id: 'overview',
    label: '概览',
    icon: 'ri-dashboard-line',
    path: '/overview',
  },
  {
    id: 'manufacturer',
    label: '厂商管理',
    icon: 'ri-building-line',
    children: [
      {
        id: 'manufacturer-list',
        label: '厂商列表',
        icon: 'ri-list-unordered',
        path: '/manufacturer/list',
      },
    ],
  },
  {
    id: 'supplier',
    label: '供应商管理',
    icon: 'ri-truck-line',
    children: [
      {
        id: 'supplier-list',
        label: '供应商列表',
        icon: 'ri-list-unordered',
        path: '/supplier/list',
      },
    ],
  },
  {
    id: 'model',
    label: '模型管理',
    icon: 'ri-cpu-line',
    children: [
      {
        id: 'model-list',
        label: '模型列表',
        icon: 'ri-list-unordered',
        path: '/model/list',
      },
      {
        id: 'model-resource',
        label: '模型资源',
        icon: 'ri-database-line',
        path: '/model/resource',
      },
      {
        id: 'category-list',
        label: '类别列表',
        icon: 'ri-folder-line',
        path: '/category/list',
      },
      {
        id: 'parameter-list',
        label: '参数列表',
        icon: 'ri-settings-line',
        path: '/parameter/list',
      },
      {
        id: 'series-list',
        label: '系列列表',
        icon: 'ri-stack-line',
        path: '/series/list',
      },
    ],
  },
  {
    id: 'user',
    label: '用户管理',
    icon: 'ri-user-line',
    children: [
      {
        id: 'user-list',
        label: '用户列表',
        icon: 'ri-list-unordered',
        path: '/user/list',
      },
      {
        id: 'user-auth',
        label: '用户认证',
        icon: 'ri-shield-check-line',
        path: '/user/auth',
      },
      {
        id: 'user-level',
        label: '用户等级',
        icon: 'ri-vip-crown-line',
        path: '/user/level',
      },
    ],
  },
  {
    id: 'order',
    label: '订单管理',
    icon: 'ri-shopping-cart-line',
    children: [
      {
        id: 'order-list',
        label: '订单列表',
        icon: 'ri-list-unordered',
        path: '/order/list',
      },
      {
        id: 'order-after-sales',
        label: '售后列表',
        icon: 'ri-customer-service-line',
        path: '/order/after-sales',
      },
    ],
  },
  {
    id: 'data',
    label: '数据中心',
    icon: 'ri-bar-chart-line',
    children: [
      {
        id: 'data-consumption',
        label: '消费数据',
        icon: 'ri-money-dollar-circle-line',
        path: '/data/consumption',
      },
      {
        id: 'data-call',
        label: '调用数据',
        icon: 'ri-phone-line',
        path: '/data/call',
      },
    ],
  },
  {
    id: 'system',
    label: '系统管理',
    icon: 'ri-settings-2-line',
    children: [
      {
        id: 'system-payment',
        label: '支付管理',
        icon: 'ri-bank-card-line',
        path: '/system/payment',
      },
      {
        id: 'system-log',
        label: '系统日志',
        icon: 'ri-file-list-line',
        path: '/system/log',
      },
    ],
  },
  {
    id: 'permission',
    label: '权限',
    icon: 'ri-shield-user-line',
    children: [
      {
        id: 'permission-role',
        label: '角色列表',
        icon: 'ri-group-line',
        path: '/permission/role',
      },
      {
        id: 'permission-user',
        label: '用户列表',
        icon: 'ri-user-settings-line',
        path: '/permission/user',
      },
    ],
  },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (path: string) => location.pathname === path;

  const isParentActive = (children: MenuItem[]) => {
    return children.some(child => child.path && isActive(child.path));
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isItemActive = item.path ? isActive(item.path) : false;
    const isParentItemActive = hasChildren ? isParentActive(item.children!) : false;

    if (hasChildren) {
      return (
        <div key={item.id}>
          <button
            onClick={() => toggleExpanded(item.id)}
            className={`w-full flex items-center justify-between px-4 py-2 text-left text-sm font-medium rounded-lg transition-colors ${
              isParentItemActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center">
              <i className={`${item.icon} w-5 h-5 mr-3`}></i>
              {item.label}
            </div>
            <i
              className={`ri-arrow-right-s-line w-4 h-4 transition-transform ${
                isExpanded ? 'rotate-90' : ''
              }`}
            ></i>
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children!.map(child => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path!}
        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          isItemActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <i className={`${item.icon} w-5 h-5 mr-3`}></i>
        {item.label}
      </Link>
    );
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
            <i className="ri-admin-line text-white"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">管理后台</h2>
        </div>
        <nav className="space-y-2">
          {menuItems.map(item => renderMenuItem(item))}
        </nav>
      </div>
    </div>
  );
};

export { Sidebar };
